export interface CourseFilters extends Record<string, unknown> {
	search?: string;
	sector?: string | string[];
	/** Matches filter field name `courseStream` → API `courseStreamIds` */
	courseStream?: string | string[];
	qualification?: string;
	/** @deprecated use courseStream; kept for backward compatibility if old filter state exists */
	courseType?: string;
	courseGroupId?: string | string[];
	courseName?: string;
	duration?: string;
	durationUnit?: string;
	durationWeeks?: string | number;
}

export interface LabEquipmentDetails {
	id: string;
	name: string;
	requiredCount: number;
}

export interface CourseFormData {
	courseName: string;
	courseType: string;
	level: string;
	duration: string;
	sector: string;
	totalStudentsCount: number;
	laboratories: LabEquipmentDetails[];
	minimumQualification: string;
	totalFacultyMemberCount: number;
	attachments: File[];
}

/** API duration unit: year, month, week, day (singular) */
export type DurationUnit = "year" | "month" | "week" | "day";

/** Exam system for term calculation: semester, year, or not applicable. */
export type ExamSystem = "semester" | "year" | "na";

/** Major in Matric: arts, science, or both (backend field: matricStream) */
export type MatricStream = "arts" | "science" | "both";

export enum StudentMinimumQualification {
	PRIMARY = "Primary",
	MIDDLE = "Middle",
	MATRIC = "Matric",
	INTERMEDIATE = "Intermediate",
	BACHELORS = "Bachelors",
}

export interface CourseLabeledAttachmentPayload {
	label: string;
	fileName: string;
}

export interface CourseAttachmentLabel {
	id: number;
	uuid: string;
	name: string;
	identifier: string;
	isRequired: boolean;
	isActive: boolean;
	sortOrder: number;
	createdAt?: string;
	updatedAt?: string;
}

export interface CourseAttachmentLabelResponse {
	success?: boolean;
	statusCode?: number;
	message?: string;
	data: CourseAttachmentLabel[];
}

export interface AssessmentBodyRef {
	id?: number;
	uuid: string;
	name: string;
	identifier?: string;
	isActive?: boolean;
}

export interface CreateCoursePayload {
	name: string;
	level?: string; // Optional - not required when teachingMode is "Conventional"
	duration: number;
	durationUnit: DurationUnit;
	sectorId: number;
	teachingModeId: number;
	courseNotifyingAuthorityId: string;
	minimumQualification?: string;
	/**
	 * Faculty qualification catalog UUID(s).
	 * Backend property name is singular `facultyQualificationId` but value is `string[]`
	 * (@IsArray, @IsUUID each, @ArrayUnique).
	 */
	facultyQualificationId?: string | string[];
	/** @deprecated Wrong property name — backend expects facultyQualificationId as string[]. */
	facultyQualificationIds?: string[];
	facultyRequirements?: string;
	experienceArea?: string;
	experienceYears?: number;
	totalFacultyMemberCount: number;
	attachments: CourseLabeledAttachmentPayload[];
	courseStreamId: string;
	labIds: number[];
	overview?: string; // Also add overview as it's in the payload
	description?: string; // Also add description as it's in the payload
	lastReview?: string; // Optional - last review date
	eligibilityCriteria?: string; // Optional - eligibility criteria
	isRegular: boolean; // Required - course mode (true for Regular, false for Non-Regular)
	courseCategoryId?: number; // Optional - course category id (POST /institutes/course)
	studentMinimumQualification?: string; // Optional - student minimum qualification
	matricStream?: MatricStream; // Optional - Major in Matric (arts | science | both)
	/** Active courses are listed/usable; backend supports on create and update. */
	isActive?: boolean;
	/** Conventional teaching mode */
	totalMarks?: number;
	practicalMarksPercent?: number;
	theoreticalMarksPercent?: number;
	/** CBTA teaching mode */
	totalModules?: number;
	/** Conventional teaching mode — equivalence (Matric, Intermediate, …) */
	courseEquivalence?: string;
	/** Shared course content metadata */
	courseCode?: string;
	trainingHours?: number;
	notificationDate?: string;
	version?: string;
	/**
	 * Competency point catalog UUID(s) — POST/PATCH/revise request body.
	 */
	competencyPointIds?: string[];
	examSystemId?: number;
	/** Number of promotion stages when exam system is Year or Semester */
	promotionStages?: number;
	/** Backend expects string count, e.g. "4". */
	termCount?: string;
	/** Optional course content subjects (UUIDs). */
	subjectIds?: string[];
	/** Assessment bodies offered for this course (UUIDs). */
	assessmentBodyIds?: string[];
	/** PVTC notifying authority — on-the-job training */
	hasOjt?: boolean;
	/** OJT duration value when hasOjt is true */
	ojtDuration?: number;
	/** OJT duration unit — UI is months-only, always send `"month"` when hasOjt is true */
	ojtDurationUnit?: DurationUnit;
	/** Course curriculum — age eligibility */
	minimumAge?: number;
	maximumAge?: number;
	relaxationPeriod?: number;
}

export interface CreateCourseResponse {
	statusCode: number;
	message: string;
	data: {
		id: string;
		name: string;
		duration: number;
		durationUnit?: string;
		totalStudentsCount: number;
		minimumQualification: string;
		totalFacultyMemberCount: number;
		courseAttachments: string[];
		courseStreamId: string;
		labIds: number[];
	};
}

export interface ValidateCourseExistencePayload {
	courseName: string;
	sectorId: number;
	duration: number;
	durationUnit: string;
	courseStreamId: number;
}

export interface ValidateCourseExistenceCourseRef {
	id?: number;
	uuid?: string;
	name?: string;
	courseCode?: string | null;
}

export interface ValidateCourseExistenceCategoryRef {
	id?: number;
	uuid?: string;
	name?: string;
	/** API may send category name as `courseName` instead of `name`. */
	courseName?: string;
	/**
	 * Course *group* number from validate-existence — NOT a `course_category` FK.
	 * Do not send this as `courseCategoryId` on create/update.
	 */
	categoryNo?: number;
}

export interface ValidateCourseExistenceResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		isExist: boolean;
		message?: string;
		/** Present when a matching category already exists for the step-1 combination. */
		category?: ValidateCourseExistenceCategoryRef | null;
		courseCategoryId?: number | null;
		courses?: ValidateCourseExistenceCourseRef[] | null;
	};
}

export interface CourseTermCountResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		termCount: string;
	};
}

//Course Level

export interface CourseLevelData {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	name: string;
	description: string;
	isActive: boolean;
}

export interface CourseLevelResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: CourseLevelData[];
}

export interface CompetencyPointData {
	id: number;
	uuid: string;
	name: string;
	isActive?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface CompetencyPointResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data:
		| CompetencyPointData[]
		| {
				data: CompetencyPointData[];
				pagination?: {
					currentPage: number;
					perPage: number;
					totalItems: number;
					totalPages: number;
				};
		  };
}

export interface FacultyQualificationData {
	id: number;
	uuid: string;
	name: string;
	isActive?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface FacultyQualificationResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data:
		| FacultyQualificationData[]
		| {
				data: FacultyQualificationData[];
				pagination?: {
					currentPage: number;
					perPage: number;
					totalItems: number;
					totalPages: number;
				};
		  };
}

export interface ExamSystemData {
	id: number;
	uuid: string;
	name: string;
	createdAt: string;
	updatedAt: string;
}

export interface ExamSystemResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: ExamSystemData[];
		pagination?: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

//Course Stream
interface CourseStreamData {
	id: string | number;
	name: string;
}

export interface CourseStreamResponse {
	statusCode: number;
	message: string;
	data: {
		data: CourseStreamData[];
	};
}

//Course Notifying Authority
interface CourseNotifyingAuthorityData {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	name: string;
	label: string;
	description: string;
	deletedAt: string | null;
}

export interface CourseNotifyingAuthorityResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: CourseNotifyingAuthorityData[];
		pagination: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

/** Course content (subject) item from GET /institutes/subjects */
export interface SubjectItem {
	id: number;
	uuid: string;
	name: string;
	description?: string | null;
	isActive: boolean;
	createdAt?: string;
	updatedAt?: string;
}

/** API response for GET /institutes/subjects */
export interface SubjectListResponse {
	success?: boolean;
	statusCode: number;
	message: string;
	data:
		| SubjectItem[]
		| {
				data: SubjectItem[];
				pagination?: {
					currentPage: number;
					perPage: number;
					totalItems: number;
					totalPages: number;
					hasNextPage: boolean;
					hasPreviousPage: boolean;
				};
		  };
	pagination?: {
		currentPage: number;
		perPage: number;
		totalItems: number;
		totalPages: number;
		hasNextPage: boolean;
		hasPreviousPage: boolean;
	};
}

/** Nested objects returned by GET /institutes/course list (when relations are included). */
export interface CourseListApiItem extends Record<string, unknown> {
	id: number;
	uuid: string;
	name: string;
	navCourseId: string | null;
	courseCode?: string | null;
	duration: number;
	durationUnit?: DurationUnit;
	type?: string;
	studentMinimumQualification?: string;
	matricStream?: string | null;
	minimumQualification: string;
	totalFacultyMemberCount: number;
	facultyRequirements?: string | null;
	experienceArea?: string | null;
	experienceYears?: number | null;
	totalStudentsCount?: number;
	sectorId?: number;
	sector?: { id: number; name: string; uuid?: string };
	courseCategoryId?: number;
	courseCategory?: { id: number; name: string; uuid?: string };
	courseGroupId?: number | null;
	versionNumber?: number | null;
	/** Content index within the course group (API: contentNo). */
	contentNo?: number | null;
	/** Content version within the group (API: contentVersionNo — CV1 in listings). */
	contentVersionNo?: number | null;
	courseSchemeId?: number | null;
	courseScheme?: { id: number; name: string; uuid?: string } | null;
	subjects?: Array<{ id?: number; uuid: string; name: string }>;
	subjectIds?: string[];
	assessmentBodies?: AssessmentBodyRef[];
	assessmentBodyIds?: string[];
	attachments?: string[] | CourseLabeledAttachmentPayload[];
	description: string;
	overview?: string;
	eligibilityCriteria?: string;
	lastReview?: string | null;
	isActive: boolean;
	status?: string;
	/** Regular vs non-regular; API may send boolean or string/number */
	isRegular?: boolean | string | number | null;
	level?: string | null;
	courseStreamId?: number;
	courseStream?: { id: number; name: string; identifier?: string; uuid?: string };
	teachingModeId?: number;
	teachingMode?: { id: number; name: string; uuid?: string };
	courseLabs?: Array<{
		id: number;
		uuid: string;
		labId: number;
		lab?: { id: number; uuid: string; name: string };
	}>;
	courseNotifyingAuthorityId?: number;
	courseNotifyingAuthority?: { uuid?: string; name?: string; label?: string };
	ownerAuthority?: { id?: number; uuid?: string; code?: string; name?: string } | null;
	addedBy?: { id?: number; uuid?: string; code?: string; name?: string; label?: string } | null;
	createdBy?: {
		id?: number;
		uuid?: string;
		firstName?: string;
		lastName?: string | null;
		organizationTypeId?: number | string | null;
		organization?: { id?: number; uuid?: string; name?: string; code?: string } | null;
		organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	} | null;
	createdById?: number | null;
	organizationTypeId?: number | string | null;
	organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	createdAt: string;
	updatedAt: string;
	totalMarks?: number | null;
	practicalMarksPercent?: number | null;
	theoreticalMarksPercent?: number | null;
	totalModules?: number | null;
	trainingHours?: number | null;
	notificationDate?: string | null;
	version?: string | null;
	courseEquivalence?: string | null;
	competencyPointIds?: string[] | null;
	/** @deprecated Prefer competencyPointIds[] on request / competencyPoints[] on response. */
	competencyPointId?: string[] | null;
	/** @deprecated Prefer competencyPoints[] — legacy single-object response. */
	competencyPoint?: { id?: number; uuid?: string; name?: string } | null;
	competencyPoints?: Array<{ id?: number; uuid?: string; name?: string }> | null;
	/** API may return a single UUID string or an array of UUIDs under this singular key. */
	facultyQualificationId?: string | string[] | null;
	facultyQualificationIds?: string[] | null;
	facultyQualification?: { id?: number; uuid?: string; name?: string } | null;
	facultyQualifications?: Array<{ id?: number; uuid?: string; name?: string }> | null;
	examSystemId?: number | null;
	examSystem?: { id: number; uuid: string; name: string } | ExamSystem | string | null;
	promotionStages?: number | null;
	termCount?: number | null;
	hasOjt?: boolean | null;
	ojtDuration?: number | null;
	minimumAge?: number | null;
	maximumAge?: number | null;
	relaxationPeriod?: number | null;
}

//Course Table (list row — matches API shape with nested relations)
export type CourseData = CourseListApiItem;

export interface CourseTableResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: CourseListApiItem[];
		pagination: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

// Course Details Response Types
export interface CourseDetailsSubjectRef {
	id?: number;
	uuid: string;
	name: string;
	addedById?: number;
	description?: string | null;
	isActive?: boolean;
}

export interface CourseDetailsContentSubjectLink {
	id?: number;
	uuid?: string;
	subject?: CourseDetailsSubjectRef | null;
	subjectId?: number;
	addedById?: number;
}

export interface CourseDetailsContentItem {
	id?: number;
	uuid?: string;
	contentCode?: string | null;
	isActive?: boolean;
	hasOjt?: boolean | null;
	duration?: number | null;
	durationUnit?: string | null;
	courseId?: number;
	subjects?: CourseDetailsSubjectRef[];
	courseContentSubjects?: CourseDetailsContentSubjectLink[];
}

export interface CourseDetailsFacultyQualificationRef {
	uuid?: string;
	name?: string;
	requiredCount?: number;
	id?: number;
}

export interface CourseDetailsData {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	name: string;
	navCourseId: string | null;
	courseCode?: string | null;
	duration: number;
	durationUnit?: string;
	type?: string | null;
	level?: string | null;
	totalStudentsCount?: number;
	minimumQualification?: string | null;
	facultyQualificationId?: string | string[] | null;
	facultyQualification?: CourseDetailsFacultyQualificationRef | null;
	facultyQualifications?: CourseDetailsFacultyQualificationRef[] | null;
	competencyPointIds?: string[] | null;
	/** @deprecated Prefer competencyPointIds[] on request / competencyPoints[] on response. */
	competencyPointId?: string[] | null;
	/** @deprecated Prefer competencyPoints[] — legacy single-object response. */
	competencyPoint?: { id?: number; uuid?: string; name?: string; description?: string | null } | null;
	competencyPoints?: Array<{ id?: number; uuid?: string; name?: string; description?: string | null }> | null;
	totalFacultyMemberCount?: number | null;
	facultyRequirements?: string | null;
	experienceArea?: string | null;
	experienceYears?: number | null;
	/** @deprecated Prefer facultyRequirements from API */
	facultyBriefRequirements?: string | null;
	/** @deprecated Prefer experienceArea from API */
	facultyExperienceArea?: string | null;
	/** @deprecated Prefer experienceYears from API */
	facultyExperienceYears?: number | null;
	attachments?: string[] | CourseLabeledAttachmentPayload[];
	description?: string | null;
	overview?: string | null;
	eligibilityCriteria?: string | null;
	lastReview?: string | null;
	isRegular?: boolean | null;
	isActive?: boolean;
	status?: string | null;
	sectorId?: number | null;
	sector?: { id?: number; name?: string; uuid?: string } | null;
	teachingModeId?: number | null;
	teachingMode?: { id?: number; name?: string; uuid?: string } | null;
	courseStream?: {
		id?: number;
		name?: string;
		uuid?: string;
		identifier?: string;
	} | null;
	courseStreamId?: number | null;
	courseLabs?: Array<{
		id: number;
		uuid: string;
		createdAt?: string;
		updatedAt?: string;
		isActive?: boolean;
		lab: {
			id: number;
			uuid: string;
			createdAt?: string;
			updatedAt?: string;
			name: string;
		};
	}> | null;
	courseNotifyingAuthority?: {
		id?: number;
		uuid?: string;
		name?: string;
		label?: string;
	} | null;
	courseNotifyingAuthorityId?: number | null;
	ownerAuthority?: { id?: number; uuid?: string; code?: string; name?: string } | null;
	addedBy?: { id?: number; uuid?: string; code?: string; name?: string; label?: string } | null;
	createdBy?: {
		id?: number;
		uuid?: string;
		firstName?: string;
		lastName?: string | null;
		organizationTypeId?: number | string | null;
		organization?: { id?: number; uuid?: string; name?: string; code?: string } | null;
		organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	} | null;
	createdById?: number | null;
	organizationTypeId?: number | string | null;
	organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	courseCategory?: {
		id?: number;
		uuid?: string;
		name?: string;
		description?: string | null;
		isActive?: boolean;
	} | null;
	courseScheme?: { id?: number; uuid?: string; name?: string } | null;
	subjects?: CourseDetailsSubjectRef[];
	/** Alternate API key for nested subject relations */
	courseSubjects?: CourseDetailsSubjectRef[];
	assessmentBodies?: AssessmentBodyRef[];
	assessmentBodyIds?: string[];
	/** Nested content blocks from GET course details */
	contents?: CourseDetailsContentItem[] | null;
	courseCategoryId?: number | null;
	courseSchemeId?: number | null;
	courseGroupId?: number | null;
	courseGroup?: {
		attachments?: string[] | CourseLabeledAttachmentPayload[];
		courseAttachments?: string[] | CourseLabeledAttachmentPayload[];
	} | null;
	/** Content index within the course group. */
	contentNo?: number | null;
	/** Content version within the group. */
	contentVersionNo?: number | null;
	versionNumber?: number | null;
	studentMinimumQualification?: string | null;
	matricStream?: MatricStream | string | null;
	examSystem?: string | { id: number; uuid?: string; name: string } | null;
	examSystemId?: number | null;
	promotionStages?: number | null;
	termCount?: string | number | null;
	totalMarks?: number | null;
	practicalMarksPercent?: number | null;
	theoreticalMarksPercent?: number | null;
	totalModules?: number | null;
	trainingHours?: number | null;
	notificationDate?: string | null;
	version?: string | null;
	courseEquivalence?: string | null;
	hasOjt?: boolean | null;
	ojtDuration?: number | null;
	ojtDurationUnit?: string | null;
	minimumAge?: number | null;
	maximumAge?: number | null;
	/** @deprecated Prefer minimumAge from API */
	minAge?: number | null;
	/** @deprecated Prefer maximumAge from API */
	maxAge?: number | null;
	relaxationPeriod?: number | null;
	tradeId?: number | null;
	technologyId?: number | null;
}

export interface CourseDetailsResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: CourseDetailsData;
}

// Course Category (GET list, POST create)
export interface CourseCategoryItem {
	id: number;
	uuid: string;
	name: string;
	isActive: boolean;
	description?: string | Record<string, unknown>;
	createdAt: string;
	updatedAt: string;
}

export interface CourseCategoryListResponse {
	message?: string;
	data: CourseCategoryItem[] | { data: CourseCategoryItem[]; pagination?: unknown };
	pagination?: {
		currentPage: number;
		perPage: number;
		totalItems: number;
		totalPages: number;
		hasNextPage: boolean;
		hasPreviousPage: boolean;
	};
}

export interface CreateCourseCategoryPayload {
	name: string;
	isActive: boolean;
	description?: string;
}

/** Row from GET /institutes/course/categories (course category / group listing). */
export interface CourseCategoryGroupItem {
	id: number;
	uuid?: string;
	/** Category index (CCI in listings). */
	categoryNo?: number;
	name: string;
	duration: number;
	durationUnit?: DurationUnit | string;
	sectorId?: number;
	sector?: { id: number; name: string; uuid?: string };
	courseStreamId?: number;
	courseStream?: { id: number; name: string; uuid?: string; identifier?: string };
	isActive?: boolean;
	totalContents?: number;
	totalVersions?: number;
	latestContentNo?: number;
	ownerAuthority?: { id?: number; uuid?: string; code?: string; name?: string; label?: string } | null;
	addedBy?: { id?: number; uuid?: string; code?: string; name?: string; label?: string } | null;
	createdBy?: {
		id?: number;
		uuid?: string;
		firstName?: string;
		lastName?: string | null;
		emailAddress?: string | null;
		organizationTypeId?: number | string | null;
		organization?: { id?: number; uuid?: string; name?: string; code?: string } | null;
		organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	} | null;
	createdById?: number | null;
	organizationTypeId?: number | string | null;
	organizationType?: { id?: number; uuid?: string; name?: string; code?: string } | null;
	createdAt?: string;
	updatedAt?: string;
	/** @deprecated Not returned by categories API — use categoryNo. */
	courseCategoryId?: number;
	/** @deprecated Not returned by categories API — use categoryNo. */
	courseGroupId?: number;
	/** @deprecated Not returned by categories API — use latestContentNo. */
	versionNumber?: number;
}

export interface CourseCategoryGroupTableResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: CourseCategoryGroupItem[];
		pagination: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

export type CourseModalMode = "create" | "edit" | "addContent" | "revise";

export interface CreateSubjectPayload {
	name: string;
}
